'use client';

import cx from 'classnames';
import { useReducedMotion } from 'motion/react';
import { useEffect, useState } from 'react';
import imageStyles from './modelImage.module.css';
import { getModelAssetSrc, getNewCarModels } from './newCarModels';
import styles from './NewCarModelSlider.module.css';

const ROTATION_INTERVAL_MS = 3000;
const SLIDE_DURATION_MS = 600;

type OwnProps = {
  make: string;
};

export default function NewCarModelSlider({ make }: OwnProps) {
  const prefersReducedMotion = useReducedMotion() ?? false;
  const models = getNewCarModels(make);
  const [activeIndex, setActiveIndex] = useState(0);
  const [exitingIndex, setExitingIndex] = useState<number | null>(null);

  useEffect(() => {
    if (prefersReducedMotion || models.length <= 1) return undefined;

    const intervalId = setInterval(() => {
      setActiveIndex(previous => {
        setExitingIndex(previous);

        return (previous + 1) % models.length;
      });
    }, ROTATION_INTERVAL_MS);

    return () => clearInterval(intervalId);
  }, [prefersReducedMotion, models.length]);

  useEffect(() => {
    if (exitingIndex === null) return;
    const timer = setTimeout(() => setExitingIndex(null), SLIDE_DURATION_MS);

    return () => clearTimeout(timer);
  }, [exitingIndex]);

  if (models.length === 0) return null;

  const visibleModels = prefersReducedMotion ? [models[0]] : models;

  const getPosition = (index: number) => {
    if (prefersReducedMotion || index === activeIndex) return 'center';
    if (index === exitingIndex) return 'left';

    return 'right';
  };

  return (
    <div
      aria-hidden="true"
      className={styles.slider}
      data-testid="new-car-model-slider"
    >
      {visibleModels.map((model, index) => (
        <img
          key={model}
          alt=""
          className={cx(imageStyles.modelImage, styles.car)}
          data-position={getPosition(index)}
          src={getModelAssetSrc(make, model)}
        />
      ))}
    </div>
  );
}
